/**
 * Per-user, per-repo merge method preference for the PR merge button.
 *
 * Stored in localStorage rather than the database: the preference is a personal
 * habit ("I always rebase in this repo"), not a team policy, and the merge
 * dropdown is already a client-only surface. A stored value is only ever a
 * preference, never a decision: getEffectiveMergeMethodFromStatus still corrects
 * it against what the repository actually allows, so a method that later gets
 * disabled degrades to an allowed one instead of failing at merge time.
 */

import type { MergeMethod } from './types';

import { MERGE_METHODS } from './types';

import { STORAGE_KEYS } from '@/lib/storage-keys';

export type MergeMethodPreferenceParams = {
  owner: string;
  repo: string;
};

export function buildMergeMethodStorageKey({ owner, repo }: MergeMethodPreferenceParams): string {
  return STORAGE_KEYS.MERGE_METHOD_PREFERENCE(owner, repo);
}

function isMergeMethod(value: unknown): value is MergeMethod {
  return typeof value === 'string' && MERGE_METHODS.includes(value as MergeMethod);
}

export function readStoredMergeMethod(params: MergeMethodPreferenceParams): MergeMethod | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(buildMergeMethodStorageKey(params));
    return isMergeMethod(raw) ? raw : null;
  } catch {
    return null;
  }
}

export function writeStoredMergeMethod(
  params: MergeMethodPreferenceParams,
  method: MergeMethod,
): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(buildMergeMethodStorageKey(params), method);
  } catch {
    // Storage can be full or blocked; the preference is not worth failing a merge over.
  }
}

/**
 * Repository-allowed methods, lowercased and in a stable display order.
 */
export function toSelectableMergeMethods(
  allowedMethods: Array<'MERGE' | 'SQUASH' | 'REBASE'> | undefined,
): MergeMethod[] {
  if (!allowedMethods?.length) {
    return [];
  }

  return MERGE_METHODS.filter((method) =>
    allowedMethods.includes(method.toUpperCase() as 'MERGE' | 'SQUASH' | 'REBASE'),
  );
}
